import { useEffect, useRef, useState } from "react";

export const usePullToRefresh = (onRefresh, threshold = 80, enable = true) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startY = useRef(0);
  const scrollTop = useRef(0);

  useEffect(() => {
    if (!enable) return;

    const handleTouchStart = (e) => {
      scrollTop.current = window.scrollY || document.documentElement.scrollTop;

      // Only activate pull-to-refresh when at the top
      if (scrollTop.current === 0) {
        startY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;

      if (diff > 0) {
        // Resistance increases as you pull further
        const resistanceFactor = 0.4 + (diff / 500) * 0.1;
        const adjustedDiff = diff * resistanceFactor;

        setPullDistance(adjustedDiff);
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      setIsPulling(false);

      if (pullDistance > threshold && onRefresh && !isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(threshold);

        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    document.addEventListener("touchstart", handleTouchStart, false);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, false);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isPulling, pullDistance, isRefreshing, onRefresh, threshold]);

  return {
    pullDistance,
    isPulling,
    isRefreshing,
  };
};
